import React, { useState } from 'react';
import { Card, Nav, Tab } from 'react-bootstrap';
import PipeVelocityCalculator from './PipeVelocityCalculator';
import IntakeScreenVelocityCalculator from './IntakeScreenVelocityCalculator';
import VacuumPrimeCalculator from './VacuumPrimeCalculator';

/**
 * EngineeringCalculators Component
 * 
 * Consolidated interface for all engineering calculation tools
 * Provides tabbed interface for easy access to pipe velocity,
 * intake screen velocity, and vacuum prime calculations
 */
const EngineeringCalculators = () => {
  const [activeCalculator, setActiveCalculator] = useState('pipeVelocity');

  return (
    <div className="engineering-calculators">
      <div className="mb-4">
        <h2>Engineering Calculators</h2>
        <p className="text-muted">
          Collection of engineering calculation tools for pipe velocity, intake screen sizing, and vacuum prime analysis.
        </p>
      </div>

      <Card>
        <Card.Header>
          <Nav variant="tabs" defaultActiveKey="pipeVelocity">
            <Nav.Item>
              <Nav.Link
                eventKey="pipeVelocity"
                active={activeCalculator === 'pipeVelocity'}
                onClick={() => setActiveCalculator('pipeVelocity')}
              >
                <i className="bi bi-diagram-3 me-2"></i>
                Pipe Velocity
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="intakeScreen"
                active={activeCalculator === 'intakeScreen'}
                onClick={() => setActiveCalculator('intakeScreen')}
              >
                <i className="bi bi-grid me-2"></i>
                Intake Screen
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="vacuumPrime"
                active={activeCalculator === 'vacuumPrime'}
                onClick={() => setActiveCalculator('vacuumPrime')}
              >
                <i className="bi bi-droplet me-2"></i>
                Vacuum Prime
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>
        <Card.Body>
          <Tab.Content>
            <Tab.Pane eventKey="pipeVelocity" active={activeCalculator === 'pipeVelocity'}>
              {activeCalculator === 'pipeVelocity' && <PipeVelocityCalculator />}
            </Tab.Pane>
            <Tab.Pane eventKey="intakeScreen" active={activeCalculator === 'intakeScreen'}>
              {activeCalculator === 'intakeScreen' && <IntakeScreenVelocityCalculator />}
            </Tab.Pane>
            <Tab.Pane eventKey="vacuumPrime" active={activeCalculator === 'vacuumPrime'}>
              {activeCalculator === 'vacuumPrime' && <VacuumPrimeCalculator />}
            </Tab.Pane>
          </Tab.Content>
        </Card.Body>
      </Card>

      {/* Calculator Descriptions */}
      <div className="row mt-4">
        <div className="col-md-4">
          <Card className="h-100">
            <Card.Body>
              <Card.Title>
                <i className="bi bi-diagram-3 me-2 text-primary"></i>
                Pipe Velocity Calculator
              </Card.Title>
              <Card.Text>
                Calculate flow velocities in pipes based on flow rate and pipe diameter. 
                Essential for sizing piping systems and ensuring optimal flow conditions.
              </Card.Text>
              <small className="text-muted">
                • Flow rate (GPM) to velocity (ft/s)<br/>
                • Multiple pipe diameter options<br/>
                • Real-time calculations
              </small>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card className="h-100">
            <Card.Body>
              <Card.Title>
                <i className="bi bi-grid me-2 text-success"></i>
                Intake Screen Calculator
              </Card.Title>
              <Card.Text>
                Determine intake screen velocities for water intake systems. 
                Critical for fish protection and intake system design compliance.
              </Card.Text>
              <small className="text-muted">
                • Screen approach velocity<br/>
                • Multiple screen configurations<br/>
                • Environmental compliance checks
              </small>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-4">
          <Card className="h-100">
            <Card.Body>
              <Card.Title>
                <i className="bi bi-droplet me-2 text-info"></i>
                Vacuum Prime Calculator
              </Card.Title>
              <Card.Text>
                Calculate vacuum requirements for pump priming operations. 
                Essential for pump system design and troubleshooting.
              </Card.Text>
              <small className="text-muted">
                • Vacuum pressure calculations<br/>
                • Priming time estimates<br/>
                • System parameter analysis
              </small>
            </Card.Body>
          </Card>
        </div>
      </div>

      <style jsx>{`
        .engineering-calculators {
          padding: 0;
        }

        .nav-tabs .nav-link {
          border: none;
          color: #6c757d;
          font-weight: 500;
        }

        .nav-tabs .nav-link.active {
          background-color: transparent;
          border: none;
          border-bottom: 3px solid #0066cc;
          color: #0066cc;
          font-weight: 600;
        }

        .nav-tabs .nav-link:hover {
          border: none;
          color: #0066cc;
          background-color: #f8f9fa;
        }

        .card {
          border: 1px solid #dee2e6;
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
          padding: 1rem 1.5rem;
        }

        .card-body {
          padding: 1.5rem;
        }

        .bi {
          font-size: 1rem;
        }
      `}</style>
    </div>
  );
};

export default EngineeringCalculators;